/**
 * e-Tesa — Generator PDF/HTML pentru Deviz Ofertă
 *
 * Modul EXTRAS din `DevizTab.jsx` (Faza 1B refactor).
 * Funcții pure (fără dependență de state React) — primesc tot ce le trebuie prin params.
 *
 * Exports:
 *  - formatDevizNumber(num, { isEUR, exchangeRate }) → string ro-RO
 *  - generateDevizPDF(params) → Promise<{ base64, filename } | void>
 *  - generateDevizPrintHTML(params) → string HTML A4
 */

import jsPDF from "jspdf";
import { addRobotoFont } from "@/utils/robotoFont";

// ─── Helper: formatare număr cu conversie EUR opțională ─────────────────────
export function formatDevizNumber(num, { isEUR = false, exchangeRate = 1 } = {}) {
  const n = Number(num) || 0;
  const converted = isEUR && exchangeRate > 0 ? n / exchangeRate : n;
  return converted.toLocaleString("ro-RO", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

// ─── Generator PDF principal ────────────────────────────────────────────────
/**
 * @param {object} params
 * @param {object} params.devizInfo - header data (numarDeviz, beneficiar, etc.)
 * @param {Array} params.workItems  - lucrările devizului
 * @param {object} params.totals    - totalurile calculate
 * @param {object} params.percentages - profit, indirecte, TVA, contributieMunca
 * @param {string} params.currencyCode - "RON" sau "EUR"
 * @param {boolean} params.isEUR   - true dacă afișăm în EUR
 * @param {number} params.exchangeRate - curs BNR pentru conversie
 * @param {boolean} [params.includeMaterials=false] - include materiale detaliate
 * @param {"save"|"view"|"print"|"email"} [params.mode="save"]
 * @param {string} [params.companyLogoUrl] - Data URI sau URL al logo-ului firmei
 * @param {string} [params.companyName] - Numele firmei (folosit pentru sigla text dacă nu există logo)
 * @param {string} [params.brandColor] - Culoare hex (#RRGGBB) pentru sigla text. Default: verde #4CAF50
 * @param {(url: string) => void} [params.onPreview] - callback pentru mode="view"
 * @param {(kind: "info"|"success"|"error", msg: string) => void} [params.notify]
 * @returns {Promise<{base64:string, filename:string}|void>}
 */
export async function generateDevizPDF(params) {
  const {
    devizInfo,
    workItems,
    totals,
    percentages,
    currencyCode,
    isEUR,
    exchangeRate,
    includeMaterials = false,
    mode = "save",
    companyLogoUrl = "",
    companyName = "",
    brandColor = "",
    onPreview,
    notify = () => {},
  } = params;

  const formatNumber = (n) => formatDevizNumber(n, { isEUR, exchangeRate });

  if (mode === "save") notify("info", "Se generează PDF-ul...");
  else if (mode === "view") notify("info", "Se pregătește previzualizarea...");
  else if (mode === "print") notify("info", "Se pregătește printarea...");

  const pdf = new jsPDF("p", "mm", "a4");

  // Load Noto Serif font for Romanian diacritics support
  const fontLoaded = await addRobotoFont(pdf);
  const fontName = fontLoaded ? "NotoSerif" : "helvetica";
  const setFont = () => pdf.setFont(fontName, "normal");

  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const margin = 15;
  let y = margin;

  // Colors
  const primaryColor = [26, 26, 46];
  const accentGreen = [76, 175, 80];
  // Convert hex (#RRGGBB) to RGB array — fallback la accentGreen dacă invalid
  const hexToRgb = (hex) => {
    const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex || "");
    return m ? [parseInt(m[1], 16), parseInt(m[2], 16), parseInt(m[3], 16)] : accentGreen;
  };
  const brandRgb = brandColor ? hexToRgb(brandColor) : accentGreen;
  // Nuanțe derivate din culoarea brand (folosite în temă, similar cu actuala paletă verde)
  const darkenRgb = (rgb, f) => [Math.round(rgb[0] * f), Math.round(rgb[1] * f), Math.round(rgb[2] * f)];
  const lightenRgb = (rgb, f) => [
    Math.round(rgb[0] * f + 255 * (1 - f)),
    Math.round(rgb[1] * f + 255 * (1 - f)),
    Math.round(rgb[2] * f + 255 * (1 - f)),
  ];
  const brandDark = darkenRgb(brandRgb, 0.45);     // pentru bara secțiunilor + capăt gradient
  const brandLight = lightenRgb(brandRgb, 0.22);   // pentru bara "Total fără TVA"
  // Luminanța percepută — alegem text alb sau negru pe badge-ul brand (pentru lizibilitate)
  const brandLum = 0.299 * brandRgb[0] + 0.587 * brandRgb[1] + 0.114 * brandRgb[2];
  const brandTextOnLight = brandLum > 165 ? [30, 30, 30] : [255, 255, 255];
  // Gradient orizontal stâng→drept între două culori, n felii
  const drawHGradient = (x, yy, w, h, leftRgb, rightRgb, steps = 28) => {
    const sliceW = w / steps;
    for (let i = 0; i < steps; i++) {
      const t = i / (steps - 1);
      pdf.setFillColor(
        Math.round(leftRgb[0] + (rightRgb[0] - leftRgb[0]) * t),
        Math.round(leftRgb[1] + (rightRgb[1] - leftRgb[1]) * t),
        Math.round(leftRgb[2] + (rightRgb[2] - leftRgb[2]) * t)
      );
      pdf.rect(x + i * sliceW, yy, sliceW + 0.35, h, "F");
    }
  };
  const lightGray = [248, 249, 250];
  const yellowBg = [255, 249, 196];
  const blueBg = [227, 242, 253];
  const lightBlueBg = [241, 248, 255];
  const blueBorder = [33, 150, 243];
  const blueText = [13, 71, 161];

  // Header (compact + slot logo în stânga)
  const headerHeight = 18;
  const headerY = y;
  pdf.setFillColor(...primaryColor);
  pdf.rect(margin, headerY, pageWidth - 2 * margin, headerHeight, "F");

  // ── Slot logo (stânga) ──────────────────────────────────────────────
  const logoBoxX = margin + 3;
  const logoBoxY = headerY + 2;
  const logoBoxW = 30;
  const logoBoxH = headerHeight - 4; // 14mm

  const drawTextSigla = () => {
    // Badge cu inițialele firmei folosind culoarea brand
    pdf.setFillColor(...brandRgb);
    pdf.roundedRect(logoBoxX, logoBoxY, logoBoxW, logoBoxH, 2, 2, "F");
    const raw = (companyName || devizInfo.executant || "")
      .toString()
      .replace(/\b(SRL|SA|SRL-D|PFA|SC|S\.R\.L\.|S\.A\.)\b/gi, "")
      .replace(/[^\p{L}\p{N}\s-]/gu, "")
      .trim();
    const parts = raw.split(/\s+/).filter(Boolean);
    let initials = "";
    if (parts.length === 0) initials = "ET";
    else if (parts.length === 1) initials = parts[0].substring(0, 3).toUpperCase();
    else initials = parts.slice(0, 3).map((p) => p[0] || "").join("").toUpperCase();
    pdf.setTextColor(...brandTextOnLight);
    pdf.setFontSize(13);
    setFont();
    pdf.text(initials, logoBoxX + logoBoxW / 2, logoBoxY + logoBoxH / 2 + 1.8, { align: "center" });
  };

  if (companyLogoUrl) {
    try {
      const fmt = /data:image\/png/i.test(companyLogoUrl) ? "PNG"
        : /data:image\/(jpeg|jpg)/i.test(companyLogoUrl) ? "JPEG"
        : /data:image\/webp/i.test(companyLogoUrl) ? "WEBP"
        : "PNG";
      let drawX = logoBoxX;
      let drawY = logoBoxY;
      let drawW = logoBoxW;
      let drawH = logoBoxH;
      try {
        const props = pdf.getImageProperties(companyLogoUrl);
        const ratio = props.width / props.height;
        if (ratio > logoBoxW / logoBoxH) {
          // wider than slot → constrain by width
          drawH = logoBoxW / ratio;
          drawW = logoBoxW;
        } else {
          // taller → constrain by height
          drawW = logoBoxH * ratio;
          drawH = logoBoxH;
        }
        drawX = logoBoxX + (logoBoxW - drawW) / 2;
        drawY = logoBoxY + (logoBoxH - drawH) / 2;
      } catch {
        // ignore — folosim cadrul integral
      }
      pdf.addImage(companyLogoUrl, fmt, drawX, drawY, drawW, drawH, undefined, "FAST");
    } catch (err) {
      console.warn("Deviz PDF: logo invalid, folosesc sigla text", err);
      drawTextSigla();
    }
  } else {
    drawTextSigla();
  }

  // ── Titlu + număr (centrat) ─────────────────────────────────────────
  pdf.setTextColor(255, 255, 255);
  pdf.setFontSize(14);
  setFont();
  const pdfTitle = includeMaterials ? "DEVIZ OFERTA - INCLUS MATERIALE" : "DEVIZ OFERTA";
  pdf.text(pdfTitle, pageWidth / 2, headerY + 9, { align: "center" });
  pdf.setFontSize(9);
  pdf.text(`${devizInfo.numarDeviz} din ${devizInfo.dataDeviz}`, pageWidth / 2, headerY + 14, { align: "center" });
  y = headerY + headerHeight + 3;

  // Info Grid (compact)
  pdf.setFillColor(...lightGray);
  pdf.roundedRect(margin, y, pageWidth - 2 * margin, 24, 2, 2, "F");
  pdf.setTextColor(0, 0, 0);
  pdf.setFontSize(8.5);
  setFont();

  const infoLeft = margin + 4;
  const infoRight = pageWidth / 2 + 4;
  let infoY = y + 5.5;

  const leftValueX = infoLeft + 22;
  const rightValueX = infoRight + 20;
  const maxLeftWidth = infoRight - leftValueX - 2;
  const maxRightWidth = pageWidth - margin - rightValueX - 2;

  const drawFitted = (text, x, yPos, maxWidth, defaultSize = 8.5, minSize = 6) => {
    const value = text == null ? "-" : String(text);
    let size = defaultSize;
    pdf.setFontSize(size);
    while (pdf.getTextWidth(value) > maxWidth && size > minSize) {
      size -= 0.25;
      pdf.setFontSize(size);
    }
    pdf.text(value, x, yPos);
    pdf.setFontSize(defaultSize);
  };

  pdf.text("Beneficiar:", infoLeft, infoY);
  drawFitted(
    `${devizInfo.beneficiar || "-"}${devizInfo.beneficiarCui ? ` (CUI: ${devizInfo.beneficiarCui})` : ""}`,
    leftValueX, infoY, maxLeftWidth
  );
  pdf.text("Obiectivul:", infoRight, infoY);
  drawFitted(devizInfo.obiectiv || "-", rightValueX, infoY, maxRightWidth);

  infoY += 6;
  pdf.text("Executant:", infoLeft, infoY);
  const executantText = devizInfo.executant
    ? `${devizInfo.executant}${devizInfo.executantCui ? ` CUI: ${devizInfo.executantCui}` : ""}`
    : "-";
  drawFitted(executantText, leftValueX, infoY, maxLeftWidth);
  pdf.text("Adresa:", infoRight, infoY);
  drawFitted(devizInfo.adresa || "-", rightValueX, infoY, maxRightWidth);

  infoY += 6;
  pdf.text("Intocmit de:", infoLeft, infoY);
  const intocmitDeText = devizInfo.intocmitDe
    ? `${devizInfo.intocmitDe}${devizInfo.intocmitDeTel ? ` tel: ${devizInfo.intocmitDeTel}` : ""}`
    : "-";
  drawFitted(intocmitDeText, leftValueX, infoY, maxLeftWidth);
  pdf.text("Stadiul fizic:", infoRight, infoY);
  drawFitted(devizInfo.stadiuFizic || "-", infoRight + 22, infoY, pageWidth - margin - (infoRight + 22) - 2);

  y += 28;

  // Section Title - Tehnică (compact, în nuanța brand închisă)
  pdf.setFillColor(...brandDark);
  pdf.roundedRect(margin, y, pageWidth - 2 * margin, 6.5, 1.5, 1.5, "F");
  pdf.setTextColor(255, 255, 255);
  pdf.setFontSize(8.5);
  setFont();
  pdf.text("SECTIUNEA TEHNICA - Capitole de Lucrari", margin + 4, y + 4.5);
  y += 9;

  // Table Header
  const colWidths = [10, 55, 15, 20, 22, 22, 22, 22];
  const tableX = margin;

  pdf.setFillColor(232, 232, 240);
  pdf.rect(tableX, y, pageWidth - 2 * margin, 6.5, "F");
  pdf.setTextColor(26, 26, 46);
  pdf.setFontSize(7);
  setFont();

  const headers = ["Nr.", "Capitol de lucrari", "U.M.", "Cant.", "Pret Mat.", "Pret Man.", "Val. Mat.", "Val. Man."];
  let xPos = tableX + 1;
  headers.forEach((h, i) => {
    pdf.text(h, xPos + colWidths[i] / 2, y + 4.5, { align: "center" });
    xPos += colWidths[i];
  });
  y += 6.5;

  // Work Items
  pdf.setFontSize(7);
  setFont();
  workItems.forEach((item, idx) => {
    const itemName = item.name || "-";
    const maxNameWidth = colWidths[1] - 4;

    const lines = pdf.splitTextToSize(itemName, maxNameWidth);
    const lineHeight = 3.5;
    const rowHeight = Math.max(5.5, lines.length * lineHeight + 2);

    if (y > pageHeight - rowHeight - 20) {
      pdf.addPage();
      y = margin;
    }

    pdf.setDrawColor(200, 200, 200);
    pdf.rect(tableX, y, pageWidth - 2 * margin, rowHeight);

    xPos = tableX + 1;
    pdf.setTextColor(0, 0, 0);

    pdf.text(String(idx + 1), xPos + colWidths[0] / 2, y + rowHeight / 2 + 1, { align: "center" });
    xPos += colWidths[0];

    pdf.rect(xPos - 1, y, colWidths[1], rowHeight);

    const textY = y + (rowHeight - lines.length * lineHeight) / 2 + lineHeight;
    lines.forEach((line, lineIdx) => {
      pdf.text(line, xPos + 1, textY + lineIdx * lineHeight);
    });
    xPos += colWidths[1];

    pdf.rect(xPos - 1, y, colWidths[2], rowHeight);
    pdf.text(item.um, xPos + colWidths[2] / 2, y + rowHeight / 2 + 1, { align: "center" });
    xPos += colWidths[2];

    [colWidths[3], colWidths[4], colWidths[5]].forEach((w, i) => {
      pdf.setFillColor(...yellowBg);
      pdf.rect(xPos - 1, y, w, rowHeight, "F");
      pdf.setDrawColor(200, 200, 200);
      pdf.rect(xPos - 1, y, w, rowHeight);
      const val = i === 0 ? String(item.quantity) : i === 1 ? formatNumber(item.materialPrice) : formatNumber(item.manoperaPrice);
      pdf.text(val, xPos + w / 2, y + rowHeight / 2 + 1, { align: "center" });
      xPos += w;
    });

    pdf.rect(xPos - 1, y, colWidths[6], rowHeight);
    pdf.text(formatNumber(item.quantity * item.materialPrice), xPos + colWidths[6] / 2, y + rowHeight / 2 + 1, { align: "center" });
    xPos += colWidths[6];

    pdf.rect(xPos - 1, y, colWidths[7], rowHeight);
    pdf.text(formatNumber(item.quantity * item.manoperaPrice), xPos + colWidths[7] / 2, y + rowHeight / 2 + 1, { align: "center" });

    y += rowHeight;

    // Materials per work item
    if (includeMaterials && item.materials && item.materials.length > 0) {
      const matSubHeaderHeight = 5;
      if (y > pageHeight - matSubHeaderHeight - 20) {
        pdf.addPage();
        y = margin;
      }

      pdf.setFillColor(...blueBg);
      pdf.rect(tableX + 10, y, pageWidth - 2 * margin - 10, matSubHeaderHeight, "F");
      pdf.setDrawColor(...blueBorder);
      pdf.rect(tableX + 10, y, pageWidth - 2 * margin - 10, matSubHeaderHeight);
      pdf.setTextColor(...blueText);
      pdf.setFontSize(6);
      setFont();
      pdf.text(`Materiale pentru lucrarea: ${itemName.substring(0, 50)}${itemName.length > 50 ? "..." : ""}`, tableX + 12, y + 3.5);
      y += matSubHeaderHeight;

      item.materials.forEach((mat) => {
        const matRowHeight = 5;
        if (y > pageHeight - matRowHeight - 15) {
          pdf.addPage();
          y = margin;
        }

        pdf.setFillColor(...lightBlueBg);
        pdf.rect(tableX + 15, y, pageWidth - 2 * margin - 15, matRowHeight, "F");
        pdf.setDrawColor(200, 220, 240);
        pdf.rect(tableX + 15, y, pageWidth - 2 * margin - 15, matRowHeight);

        pdf.setTextColor(60, 60, 80);
        pdf.setFontSize(6);
        setFont();

        const matName = mat.name || mat.item_name || "-";
        const matUm = mat.um || "buc";
        const matQty = mat.quantity || 0;
        const matPrice = mat.price || mat.material_price || 0;
        const matValue = matQty * matPrice;

        pdf.text(`• ${matName}`, tableX + 17, y + 3.5);
        pdf.text(`${matQty} ${matUm}`, tableX + 110, y + 3.5);
        pdf.text(`${formatNumber(matPrice)} ${currencyCode}/um`, tableX + 135, y + 3.5);
        pdf.text(`= ${formatNumber(matValue)} ${currencyCode}`, tableX + 165, y + 3.5);

        y += matRowHeight;
      });

      const totalMatForItem = item.materials.reduce((sum, m) => sum + (m.quantity || 0) * (m.price || m.material_price || 0), 0);
      const totalRowHeight = 5;
      pdf.setFillColor(187, 222, 251);
      pdf.rect(tableX + 15, y, pageWidth - 2 * margin - 15, totalRowHeight, "F");
      pdf.setDrawColor(...blueBorder);
      pdf.rect(tableX + 15, y, pageWidth - 2 * margin - 15, totalRowHeight);
      pdf.setTextColor(...blueText);
      pdf.setFontSize(6);
      setFont();
      pdf.text(`Total materiale lucrare: ${formatNumber(totalMatForItem)} ${currencyCode}`, tableX + 17, y + 3.5);
      y += totalRowHeight + 2;
    }
  });

  y += 4;
  if (y > pageHeight - 80) {
    pdf.addPage();
    y = margin;
  }

  // Financial Section (compact, în nuanța brand închisă)
  pdf.setFillColor(...brandDark);
  pdf.roundedRect(margin, y, pageWidth - 2 * margin, 6.5, 1.5, 1.5, "F");
  pdf.setTextColor(255, 255, 255);
  pdf.setFontSize(8.5);
  setFont();
  pdf.text("SECTIUNEA FINANCIARA - Recapitulatie Valorica", margin + 4, y + 4.5);
  y += 9;

  const finColWidths = [90, 30, 30, 30];
  const finTableWidth = finColWidths.reduce((a, b) => a + b, 0);
  const finTableX = margin;

  pdf.setFillColor(232, 232, 240);
  pdf.rect(finTableX, y, finTableWidth, 5.5, "F");
  pdf.setTextColor(26, 26, 46);
  pdf.setFontSize(7.5);
  setFont();

  xPos = finTableX;
  ["Denumire", "Material", "Manopera", "Total"].forEach((h, i) => {
    pdf.text(h, xPos + (i === 0 ? 3 : finColWidths[i] / 2), y + 4, { align: i === 0 ? "left" : "center" });
    xPos += finColWidths[i];
  });
  y += 5.5;

  const addFinRow = (label, mat, man, total, bgColor = null) => {
    if (y > pageHeight - 20) {
      pdf.addPage();
      y = margin;
    }
    const rowH = 5;
    if (bgColor) {
      pdf.setFillColor(...bgColor);
      pdf.rect(finTableX, y, finTableWidth, rowH, "F");
    }
    pdf.setDrawColor(200, 200, 200);
    pdf.rect(finTableX, y, finTableWidth, rowH);
    pdf.setTextColor(0, 0, 0);
    pdf.setFontSize(7);
    setFont();
    xPos = finTableX;
    pdf.text(label, xPos + 3, y + 3.5);
    xPos += finColWidths[0];
    pdf.text(mat, xPos + finColWidths[1] - 3, y + 3.5, { align: "right" });
    xPos += finColWidths[1];
    pdf.text(man, xPos + finColWidths[2] - 3, y + 3.5, { align: "right" });
    xPos += finColWidths[2];
    pdf.text(total, xPos + finColWidths[3] - 3, y + 3.5, { align: "right" });
    y += rowH;
  };

  addFinRow("Cheltuieli directe", formatNumber(totals.totalMaterial), formatNumber(totals.totalManopera), formatNumber(totals.totalDirect), [227, 242, 253]);
  addFinRow(`+ Contributie munca (${percentages.contributieMunca}%)`, "-", formatNumber(totals.contributieMunca), formatNumber(totals.contributieMunca));
  addFinRow(`+ Cheltuieli indirecte (${percentages.cheltuieliIndirecte}%)`, formatNumber(totals.cheltuieliIndirecteMaterial), formatNumber(totals.cheltuieliIndirecteManopera), formatNumber(totals.cheltuieliIndirecteMaterial + totals.cheltuieliIndirecteManopera));
  addFinRow(`+ Profit (${percentages.profit}%)`, formatNumber(totals.profitMaterial), formatNumber(totals.profitManopera), formatNumber(totals.profitMaterial + totals.profitManopera));

  // Total fără TVA (nuanță brand deschis)
  pdf.setFillColor(...brandLight);
  pdf.rect(finTableX, y, finTableWidth, 6.5, "F");
  pdf.setDrawColor(100, 100, 100);
  pdf.rect(finTableX, y, finTableWidth, 6.5);
  pdf.setTextColor(0, 0, 0);
  pdf.setFontSize(8.5);
  setFont();
  pdf.text("TOTAL GENERAL (fara TVA)", finTableX + 3, y + 4.5);
  pdf.text(formatNumber(totals.totalFaraTVA) + " " + currencyCode, finTableX + finTableWidth - 3, y + 4.5, { align: "right" });
  y += 6.5;

  addFinRow(`TVA (${percentages.tva}%)`, "", "", formatNumber(totals.tvaValue) + " " + currencyCode);

  // Grand Total (gradient brand → brandDark)
  drawHGradient(finTableX, y, finTableWidth, 8.5, brandRgb, brandDark);
  pdf.setTextColor(...brandTextOnLight);
  pdf.setFontSize(10.5);
  setFont();
  pdf.text("TOTAL GENERAL (inclusiv TVA)", finTableX + 3, y + 6);
  pdf.text(formatNumber(totals.totalCuTVA) + " " + currencyCode, finTableX + finTableWidth - 3, y + 6, { align: "right" });
  y += 12;

  // Signatures
  if (y > pageHeight - 40) {
    pdf.addPage();
    y = margin;
  }
  const sigWidth = 60;
  const sigX1 = margin + 10;
  const sigX2 = pageWidth - margin - sigWidth - 10;

  pdf.setTextColor(0, 0, 0);
  pdf.setFontSize(10);
  setFont();
  pdf.text("BENEFICIAR", sigX1 + sigWidth / 2, y, { align: "center" });
  pdf.text("EXECUTANT", sigX2 + sigWidth / 2, y, { align: "center" });

  y += 15;
  pdf.setFontSize(9);
  pdf.text(devizInfo.beneficiar || "____________________", sigX1 + sigWidth / 2, y, { align: "center" });
  pdf.text(devizInfo.executant || "____________________", sigX2 + sigWidth / 2, y, { align: "center" });

  y += 5;
  pdf.setDrawColor(100, 100, 100);
  pdf.line(sigX1, y, sigX1 + sigWidth, y);
  pdf.line(sigX2, y, sigX2 + sigWidth, y);

  y += 4;
  pdf.setFontSize(7);
  pdf.setTextColor(100, 100, 100);
  pdf.text("Semnatura si stampila", sigX1 + sigWidth / 2, y, { align: "center" });
  pdf.text("Semnatura si stampila", sigX2 + sigWidth / 2, y, { align: "center" });

  // Observații
  if (devizInfo.observatii && devizInfo.observatii.trim()) {
    y += 12;
    if (y > pageHeight - 35) {
      pdf.addPage();
      y = margin;
    }

    pdf.setFontSize(9);
    pdf.setTextColor(0, 0, 0);
    setFont("bold");
    pdf.text("Observații:", margin, y);

    y += 5;
    setFont();
    pdf.setFontSize(8.5);
    pdf.setTextColor(60, 60, 60);
    const obsLines = pdf.splitTextToSize(devizInfo.observatii.trim(), pageWidth - 2 * margin);
    pdf.text(obsLines, margin, y);
    y += obsLines.length * 4;
  }

  // Footer
  pdf.setFontSize(7);
  const footerPrefix = `Deviz "${devizInfo.numarDeviz}" generat cu `;
  const footerLink = "e-Tesa";
  const footerSuffix = ` la ${new Date().toLocaleString("ro-RO", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const wPrefix = pdf.getTextWidth(footerPrefix);
  const wLink = pdf.getTextWidth(footerLink);
  const wSuffix = pdf.getTextWidth(footerSuffix);
  const footerX = pageWidth / 2 - (wPrefix + wLink + wSuffix) / 2;
  const footerY = pageHeight - 10;
  pdf.setTextColor(150, 150, 150);
  pdf.text(footerPrefix, footerX, footerY);
  pdf.setTextColor(29, 78, 216);
  pdf.textWithLink(footerLink, footerX + wPrefix, footerY, { url: "https://e-tesa.ro" });
  pdf.setTextColor(150, 150, 150);
  pdf.text(footerSuffix, footerX + wPrefix + wLink, footerY);

  // Output dispatch
  const beneficiarName = (devizInfo.beneficiar || "fara_beneficiar").toString();
  const safeBeneficiarName = beneficiarName.replace(/[^a-zA-Z0-9\u00C0-\u024F]/g, "_").substring(0, 40);
  const fileName = `Deviz_${devizInfo.numarDeviz}_${new Date().toLocaleDateString("ro-RO").replace(/\./g, ".")}_${safeBeneficiarName}.pdf`;

  if (mode === "view") {
    const blobUrl = pdf.output("bloburl");
    if (typeof onPreview === "function") onPreview(blobUrl);
    return;
  }
  if (mode === "print") {
    const blob = pdf.output("blob");
    const blobUrl = URL.createObjectURL(blob);
    const printWin = window.open(blobUrl, "_blank");
    if (printWin) {
      setTimeout(() => {
        try {
          printWin.focus();
          printWin.print();
        } catch {
          /* ignore */
        }
      }, 800);
    } else {
      notify("error", "Permite pop-up-urile pentru a printa.");
    }
    return;
  }
  if (mode === "email") {
    const dataUri = pdf.output("datauristring");
    const base64 = dataUri.split(",")[1] || "";
    return { base64, filename: fileName };
  }
  pdf.save(fileName);
  notify("success", "PDF generat cu succes!");
}

// ─── Generator HTML pentru print/preview ────────────────────────────────────
/**
 * Returnează HTML A4 pentru afișare în iframe sau previzualizare.
 * Identic vizual cu PDF-ul jsPDF dar bazat pe CSS.
 */
export function generateDevizPrintHTML(params) {
  const { devizInfo, workItems, totals, percentages, currencyCode, isEUR, exchangeRate } = params;
  const formatNumber = (n) => formatDevizNumber(n, { isEUR, exchangeRate });

  return `
    <!DOCTYPE html>
    <html lang="ro">
    <head>
      <meta charset="UTF-8">
      <title>Deviz F3 - ${devizInfo.numarDeviz}</title>
      <style>
        @page { size: A4 portrait; margin: 15mm; }
        * { box-sizing: border-box; }
        body { 
          font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; 
          font-size: 10px; 
          line-height: 1.4;
          color: #1a1a2e;
          margin: 0;
          padding: 15px;
          background: #fff;
        }
        .header { text-align: center; margin-bottom: 20px; padding-bottom: 15px; border-bottom: 3px solid #1a1a2e; }
        .header h1 { font-size: 18px; margin: 0 0 5px 0; color: #1a1a2e; text-transform: uppercase; letter-spacing: 2px; }
        .header h2 { font-size: 14px; margin: 0; color: #4a4a6a; font-weight: normal; }
        .header .subtitle { font-size: 11px; color: #666; margin-top: 5px; }
        .info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; margin-bottom: 20px; background: #f8f9fa; padding: 15px; border-radius: 8px; }
        .info-item { display: flex; }
        .info-label { font-weight: 600; color: #1a1a2e; min-width: 100px; }
        .info-value { color: #333; }
        .section-title { background: linear-gradient(135deg, #1a1a2e 0%, #2d2d4a 100%); color: white; padding: 8px 15px; margin: 20px 0 10px 0; font-weight: 600; font-size: 11px; text-transform: uppercase; letter-spacing: 1px; border-radius: 4px; }
        table { width: 100%; border-collapse: collapse; margin-bottom: 15px; font-size: 9px; }
        th { background: #e8e8f0; color: #1a1a2e; padding: 8px 5px; text-align: center; font-weight: 600; border: 1px solid #ccc; text-transform: uppercase; font-size: 8px; }
        td { padding: 6px 5px; border: 1px solid #ddd; vertical-align: middle; }
        .text-right { text-align: right; }
        .text-center { text-align: center; }
        .text-left { text-align: left; }
        .yellow-cell { background: #fff9c4 !important; }
        .total-row { background: #e3f2fd; font-weight: 600; }
        .subtotal-row { background: #f5f5f5; }
        .grand-total { background: linear-gradient(135deg, #4caf50 0%, #2e7d32 100%); color: white; font-size: 11px; }
        .grand-total td { border-color: #2e7d32; padding: 10px 5px; }
        .recap-table td:first-child { text-align: left; padding-left: 10px; }
        .indent { padding-left: 25px !important; }
        .signatures { display: flex; justify-content: space-between; margin-top: 40px; padding-top: 20px; }
        .signature-box { width: 200px; text-align: center; }
        .signature-title { font-weight: 600; color: #1a1a2e; margin-bottom: 50px; font-size: 11px; }
        .signature-name { border-top: 1px solid #333; padding-top: 5px; font-size: 10px; color: #333; }
        .signature-line { font-size: 9px; color: #666; margin-top: 3px; }
        .footer { margin-top: 30px; padding-top: 15px; border-top: 1px solid #ddd; font-size: 8px; color: #888; text-align: center; }
        @media print { body { padding: 0; } .no-print { display: none; } }
      </style>
    </head>
    <body>
      <div class="header">
        <h1>DEVIZ OFERTĂ</h1>
        <div class="subtitle">${devizInfo.numarDeviz} din ${devizInfo.dataDeviz}</div>
        ${devizInfo.contractNumber ? `<div class="subtitle" style="color:#2563eb; margin-top:3px;">Aferent contractului CTR ${devizInfo.contractNumber} din ${devizInfo.contractDate}</div>` : ""}
      </div>
      
      <div class="info-grid">
        <div class="info-item"><span class="info-label">Beneficiar:</span><span class="info-value">${devizInfo.beneficiar || "-"}${devizInfo.beneficiarCui ? ` (CUI: ${devizInfo.beneficiarCui})` : ""}</span></div>
        <div class="info-item"><span class="info-label">Obiectivul:</span><span class="info-value">${devizInfo.obiectiv || "-"}</span></div>
        <div class="info-item"><span class="info-label">Executant:</span><span class="info-value">${devizInfo.executant || "Nume executant"}${devizInfo.executantCui ? ` CUI: ${devizInfo.executantCui}` : ""}</span></div>
        <div class="info-item"><span class="info-label">Adresa:</span><span class="info-value">${devizInfo.adresa || "-"}</span></div>
        <div class="info-item"><span class="info-label">Întocmit de:</span><span class="info-value">${devizInfo.intocmitDe || "-"}${devizInfo.intocmitDeTel ? ` tel: ${devizInfo.intocmitDeTel}` : ""}</span></div>
        <div class="info-item"><span class="info-label">Stadiul fizic:</span><span class="info-value">${devizInfo.stadiuFizic || "-"}</span></div>
      </div>

      <div class="section-title">Secțiunea Tehnică - Capitole de Lucrări</div>
      
      <table>
        <thead>
          <tr>
            <th style="width:30px">Nr.</th>
            <th>Capitol de lucrări / Descriere</th>
            <th style="width:40px">U.M.</th>
            <th style="width:60px">Cantitate</th>
            <th style="width:70px">Preț Material</th>
            <th style="width:70px">Preț Manoperă</th>
            <th style="width:80px">Val. Material</th>
            <th style="width:80px">Val. Manoperă</th>
            <th style="width:80px">Total</th>
          </tr>
        </thead>
        <tbody>
          ${workItems.map((item, idx) => `
          <tr>
            <td class="text-center">${idx + 1}</td>
            <td class="text-left">${item.name || "-"}</td>
            <td class="text-center">${item.um}</td>
            <td class="text-right yellow-cell">${item.quantity}</td>
            <td class="text-right yellow-cell">${formatNumber(item.materialPrice)}</td>
            <td class="text-right yellow-cell">${formatNumber(item.manoperaPrice)}</td>
            <td class="text-right">${formatNumber(item.quantity * item.materialPrice)}</td>
            <td class="text-right">${formatNumber(item.quantity * item.manoperaPrice)}</td>
            <td class="text-right">${formatNumber(item.quantity * (item.materialPrice + item.manoperaPrice))}</td>
          </tr>
          `).join("")}
        </tbody>
      </table>

      <div class="section-title">Secțiunea Financiară - Recapitulație Valorică</div>
      
      <table class="recap-table">
        <thead>
          <tr>
            <th style="width:50%">Denumire</th>
            <th style="width:16%">Material (${currencyCode})</th>
            <th style="width:16%">Manoperă (${currencyCode})</th>
            <th style="width:18%">Total (${currencyCode})</th>
          </tr>
        </thead>
        <tbody>
          <tr class="total-row">
            <td>Cheltuieli directe</td>
            <td class="text-right">${formatNumber(totals.totalMaterial)}</td>
            <td class="text-right">${formatNumber(totals.totalManopera)}</td>
            <td class="text-right">${formatNumber(totals.totalDirect)}</td>
          </tr>
          <tr>
            <td class="indent">+ Contribuție muncă (${percentages.contributieMunca}%)</td>
            <td class="text-right">-</td>
            <td class="text-right">${formatNumber(totals.contributieMunca)}</td>
            <td class="text-right">${formatNumber(totals.contributieMunca)}</td>
          </tr>
          <tr>
            <td class="indent">+ Cheltuieli indirecte (${percentages.cheltuieliIndirecte}%)</td>
            <td class="text-right">${formatNumber(totals.cheltuieliIndirecteMaterial)}</td>
            <td class="text-right">${formatNumber(totals.cheltuieliIndirecteManopera)}</td>
            <td class="text-right">${formatNumber(totals.cheltuieliIndirecteMaterial + totals.cheltuieliIndirecteManopera)}</td>
          </tr>
          <tr>
            <td class="indent">+ Profit (${percentages.profit}%)</td>
            <td class="text-right">${formatNumber(totals.profitMaterial)}</td>
            <td class="text-right">${formatNumber(totals.profitManopera)}</td>
            <td class="text-right">${formatNumber(totals.profitMaterial + totals.profitManopera)}</td>
          </tr>
          <tr class="total-row" style="background:#c8e6c9">
            <td colspan="3"><strong>TOTAL GENERAL (fără TVA)</strong></td>
            <td class="text-right"><strong>${formatNumber(totals.totalFaraTVA)}</strong></td>
          </tr>
          <tr>
            <td colspan="3">TVA (${percentages.tva}%)</td>
            <td class="text-right">${formatNumber(totals.tvaValue)}</td>
          </tr>
          <tr class="grand-total">
            <td colspan="3"><strong>TOTAL GENERAL (inclusiv TVA)</strong></td>
            <td class="text-right"><strong>${formatNumber(totals.totalCuTVA)} ${currencyCode}</strong></td>
          </tr>
        </tbody>
      </table>

      <div class="signatures">
        <div class="signature-box">
          <div class="signature-title">BENEFICIAR</div>
          <div class="signature-name">${devizInfo.beneficiar || "____________________"}</div>
          <div class="signature-line">Semnătură și ștampilă</div>
        </div>
        <div class="signature-box">
          <div class="signature-title">EXECUTANT</div>
          <div class="signature-name">${devizInfo.executant || "____________________"}</div>
          <div class="signature-line">Semnătură și ștampilă</div>
        </div>
      </div>

      ${devizInfo.observatii && devizInfo.observatii.trim() ? `
      <div style="margin-top: 25px; padding-top: 12px; border-top: 1px solid #e0e0e0;">
        <div style="font-weight: bold; font-size: 11px; color: #000; margin-bottom: 6px;">Observații:</div>
        <div style="font-size: 10px; color: #333; line-height: 1.5; white-space: pre-wrap;">${devizInfo.observatii.replace(/</g, "&lt;").replace(/>/g, "&gt;")}</div>
      </div>
      ` : ""}

      <div class="footer">
        <p>Deviz "${devizInfo.numarDeviz}" generat cu <a href="https://e-tesa.ro" target="_blank" style="color: #1d4ed8; text-decoration: underline; font-weight: 500;">e-Tesa</a> la ${new Date().toLocaleString("ro-RO", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
      </div>
    </body>
    </html>
  `;
}
